<template>
  <div @click="handleAutocompleteClick" @keyup.enter="handleAutocompleteClick">
  <v-autocomplete
    :value="readOnly ? selectedAdGroups : value"
    :items="adGroupSelections"
    :item-text="item => isDC(item) ? item.party_name : item.name"
    :item-value="item => isDC(item) ? item.party_id : item.id"
    item-color="action"
    label="Ad Groups*"
    :clearable="!readOnly"
    chips
    small-chips
    background-color="#fff"
    :rules="[v => (Array.isArray(v) && v.length > 0) || 'Required field']"
    validate-on-blur
    :readonly="readOnly"
    :disabled="!readOnly && allAdGroupsRequired"
    :dense="!readOnly"
    multiple
    return-object
    :outlined="!readOnly"
    hide-details="auto"
    @change="handleChange">
    <template v-slot:prepend-item>
      <v-list-item>
        <v-list-item-action @click="handleSelectAllClick">
          <v-checkbox :value="allAdGroupsSelected">
            <template v-slot:label>
              <span class="text-subtitle-2 ml-8 text-black font-weight-bold text--black">
                Select All
              </span>
            </template>
          </v-checkbox>
        </v-list-item-action>
      </v-list-item>
      <v-divider/>
    </template>
    <template v-slot:item="{ item, attrs: { inputValue, disabled, ripple } }">
      <div style="width: 100%">
        <template>
          <v-list-item class="ma-0 pa-0" :disabled="disabled">
            <v-list-item-action>
              <v-checkbox color="action" :input-value="inputValue" :disabled="disabled" :ripple="ripple"/>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>
                {{ isDC(item) ? item.party_name : item.name }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template> 
      </div>
    </template>
    <template v-slot:selection="{ item, index }">
      <v-chip v-if="allAdGroupsSelected && index === 1" small>
        <span> All Groups Selected </span>
      </v-chip>
      <v-chip v-else-if="!allAdGroupsSelected && selectedAdGroups.includes(item) && selectedAdGroups.indexOf(item) < 3" small>
        <span> 
          {{ isDC(item) ? item.party_name : item.name }}
        </span> 
      </v-chip>
      <v-chip v-else-if="selectedAdGroups.indexOf(item) === 3 && !allAdGroupsSelected" small>
        <span> {{ `+${selectedAdGroups.length - subtractedHeaders}` }} </span>
      </v-chip>
    </template>
    </v-autocomplete>
  <AdGroupsModal
    v-if="adGroupModal"
    :value="adGroupModal"
    :selectedAdGroups="value"
    @closeDialog="closeAdGroupModal"
  >
  </AdGroupsModal>
</div>
</template>
<script>
// Components
const AdGroupsModal = () => import('@/components/contracts/AdGroupsModal.vue')
// Mixins
import { userAccess } from '@/mixins/user-access'
import { utils } from '@/mixins/utils'
export default {
  data() {
    return {
      allAdGroupsSelected: false,
      adGroupModal: false,
      internalUpdate: false,
      initialized: false,
      adGroupList: [],
      updatedList: [],
    }
  },
  name: 'ContractAdGroups',
  components: { AdGroupsModal },
  mixins: [userAccess, utils],
  props: {
    value: Array,
    readOnly: Boolean,
    reupload: Boolean,
    adDatesSelected: Boolean,
    manuallyAdded: Array,
    allAdGroupsRequired: {
      type: Boolean,
      default: false
    }
  },
  created() {
    if (this.reupload) {
      this.handleReuploadValues()
    }
    this.initialized = true;
  },
  watch: {
    value: {
      async handler(newValue, oldValue) {
        if (this.internalUpdate) {
        this.internalUpdate = false;
        return;
      }
        const removedValue = oldValue.filter(o => !newValue.includes(o))[0]
        if (this.isDistributionCenterAdded(newValue, oldValue) || this.isRegionGroupAdded(newValue, oldValue)) {
          this.handleParentGroupAdd(newValue, oldValue)
        } else if (this.isAdGroupAdded(newValue, oldValue)) {
          this.handleAddAdGroup(newValue, oldValue)
        } else if (this.isItemRemoved(newValue, oldValue)) {
          this.handleRemoveItem(newValue, oldValue, removedValue)
        }
        
        if (this.allAdGroupsSelected && newValue.length < oldValue.length) {
          this.allAdGroupsSelected = false
        } else if (this.$store.getters.activeDistributionCenters.every(dc => this.updatedList.filter(v => v?.party_type?.constant === "DISTRIBUTION_CENTER").includes(dc))) {
          this.allAdGroupsSelected = true
        }
      }
    },
    allAdGroupsRequired: {
      handler(newValue) {
        if (newValue === true && this.allAdGroupsSelected) return
        this.internalUpdate = true
        this.handleSelectAllClick()
        this.allAdGroupsSelected = newValue
      }
    }
  },
  computed: {
    activeAdGroups () {
      let groups = this.$store.getters.adGroups.filter((g) => {
        return g?.attributes?.AD_GROUP_IS_ACTIVE === 'True'
          && !this.totalPromoExcludedAdGroups.some(ag => ag.id === g.id)
      })
      if (this.limitAccessByRelatedStores) {
        groups = groups.filter(g => this.userRelatedPartyIds.includes(g.id))
      }
      groups = [{header: "Ad Groups"}, ...groups]
      return this.sortByKey(groups, 'name')
    },
    subtractedHeaders() {
      return this.adGroupSelections.filter(a => a.header != null).length
    },
    totalPromoExcludedAdGroups() {
      return this.$store.getters.totalPromoExcludedAdGroups
    },
    adPromoOnlyExcludedAdGroups() {
      return this.$store.getters.adPromoOnlyExcludedAdGroups
    },
    excludedAdGroups() {
      return this.adDatesSelected 
        ? [...this.adPromoOnlyExcludedAdGroups, ...this.totalPromoExcludedAdGroups]
        : this.totalPromoExcludedAdGroups
    },
    distributionCenters () {
      return[{header: 'Distribution Centers'}, ...this.$store.getters.activeDistributionCenters]
    },
    regionGroups () {
      return[{header: 'Region Groups'}, ...this.$store.getters.activeRegionGroups]
    },
    adGroupSelections () {
      return this.tenantUsesFullContractUpload ? [...this.distributionCenters, ...this.regionGroups, ...this.activeAdGroups] : [...this.activeAdGroups]
    },
    selectedAdGroups() {
      if (this.readOnly || (this.reupload && !this.initialized)) {
            const distributionCenters = this.distributionCenters.filter(dc => dc.member_ids?.every(mi => this.value.includes(mi)))
            const regionGroups = this.regionGroups.filter(rg => rg.member_ids?.every(mi => this.value.includes(mi)))
            const adGroups = this.value.flatMap(v => {
            const group = this.activeAdGroups.find(ag => ag.id === v && (!distributionCenters.some(dc => dc.member_ids.includes(v)) && !regionGroups.some(rg => rg.member_ids.includes(v))));
            return group ? group : [];
          });
        return this.sortByItemName([...regionGroups, ...distributionCenters, ...adGroups])
      } else {
        const distributionCenters = this.value.filter(v => v?.party_type?.constant === 'DISTRIBUTION_CENTER')
        const regionGroups = this.value.filter(v => v?.party_type?.constant === 'REGION_GROUP')
        const adGroups = this.value.filter(v => v?.party_type?.constant === 'AD_GROUP' && (!distributionCenters.some(dc => dc.member_ids.includes(v.id)) && !regionGroups.some(rg => rg.member_ids.includes(v.id))))
        return this.sortByItemName([...regionGroups, ...distributionCenters, ...adGroups])
      }
    },
  },
  methods: {
    handleAutocompleteClick() {
      if (this.readOnly) {
        this.adGroupModal = true
      }
    },
    handleSelectAllClick() {
      if (!this.allAdGroupsSelected) {
        let allDCs = this.$store.getters.activeDistributionCenters
        let allRGs = this.$store.getters.activeRegionGroups
        let adGroups = this.activeAdGroups
        this.$emit('update:value', [...allRGs, ...allDCs, ...adGroups])
      } else {
        this.$emit('update:value', [])
        this.$emit('update:manuallyAdded', [])
      }
    },
    handleReuploadValues() {
      const distributionCenters = this.distributionCenters.filter(dc => dc.member_ids?.every(mi => this.value.includes(mi)))
      const regionGroups = this.regionGroups.filter(rg => rg.member_ids?.every(mi => this.value.includes(mi)))
      const adGroups = this.value.flatMap(v => {
      const group = this.activeAdGroups.find(ag => ag.id === v);
      return group ? [group] : [];});
      this.$emit('update:value', [...regionGroups, ...distributionCenters, ...adGroups])
    },
    handleChange(event) {
      this.$emit('update:value', event)
    },
    closeAdGroupModal() {
      this.adGroupModal = false
    },
    isDC(item) {
      return item?.party_type === 'DISTRIBUTION_CENTER'
    },
    findObject(item) {
      return this.isDC(item) ? this.activeDistributionCenters.find(dc => item.id === dc.id) : this.activeAdGroups.find(ag => item.id === ag.id)
    },
    isDistributionCenterAdded(newValue, oldValue) {
      return newValue[newValue.length - 1]?.party_type?.constant === 'DISTRIBUTION_CENTER' && newValue.length > oldValue.length
    },
    isRegionGroupAdded(newValue, oldValue) {
      return newValue[newValue.length - 1]?.party_type?.constant === 'REGION_GROUP' && newValue.length > oldValue.length
    },
    isAdGroupAdded(newValue, oldValue) {
      return newValue[newValue.length - 1]?.party_type?.constant === 'AD_GROUP' && newValue.length > oldValue.length
    },
    isItemRemoved(newValue, oldValue) {
      return newValue.length < oldValue.length && newValue.length > 0
    },
    handleAddAdGroup(newValue) {
      const groupToAdd = newValue[newValue.length-1]
      const parentGroups = [...this.regionGroups, ...this.distributionCenters].filter(g => (g?.member_ids?.includes(groupToAdd.id)))
      const groupsToAdd = parentGroups.filter(g => g?.member_ids?.every(mi => this.value.map(v => v.id).includes(mi)))
      if (this.excludedAdGroups.includes(groupToAdd)) {
        this.$emit('update:manuallyAdded', [...this.manuallyAdded, groupToAdd])
      }
      if (groupsToAdd.length > 0) {
        this.updatedList = this.sortByItemName(Array.from(new Set([...groupsToAdd, ...newValue])))
      } else {
        this.updatedList = this.sortByItemName(newValue)
      }
      this.$emit('update:value', this.updatedList)
    },
    handleParentGroupAdd(newValue) {
      let adGroups = newValue[newValue.length - 1].member_ids.map(mi => this.activeAdGroups.find(ag => ag.id === mi))
      const parentGroupsToAdd = [...this.distributionCenters, ...this.regionGroups].filter(group => (group?.member_ids?.every(mi => newValue[newValue.length - 1].member_ids.includes(mi))))
      this.updatedList = this.sortByItemName(Array.from(new Set([...parentGroupsToAdd, ...adGroups, ...newValue.map(item => {return item})])))
      
      this.internalUpdate = true
      this.$emit('update:value', this.updatedList)
    },
    handleRemoveItem(newValue, oldValue, removedValue) {
      if (removedValue?.party_type?.constant === 'DISTRIBUTION_CENTER' || removedValue?.party_type?.constant === 'REGION_GROUP') {
        const adGroupsToRemove = removedValue.member_ids.map(mi => this.activeAdGroups.find(ag => ag.id === mi))
        if (adGroupsToRemove.some(ag => this.excludedAdGroups.includes(ag))) {
          this.$emit('update:manuallyAdded', this.manuallyAdded.filter(m => !adGroupsToRemove.includes(m)))
        }
        const parentGroupsToRemove = [...this.distributionCenters, ...this.regionGroups].filter(group => (group?.member_ids?.some(mi => removedValue.member_ids.includes(mi))))
        this.updatedList = this.sortByItemName(oldValue.filter(ag => ![removedValue, ...adGroupsToRemove, ...parentGroupsToRemove].includes(ag)))

        this.internalUpdate = true
        this.$emit('update:value', this.updatedList)
      } else {
        const parentGroupsToRemove = [...this.distributionCenters, ...this.regionGroups].filter(group => (group?.member_ids?.includes(removedValue.id)))
        this.updatedList = this.sortByItemName(newValue.filter(value => !parentGroupsToRemove.map(g => g.party_id).includes(value?.id || value)))
        this.internalUpdate = true
        this.$emit('update:value', this.updatedList)
      }
    },
    sortByItemName(array) {
      return array.sort((a,b) => a?.name?.localeCompare(b.name))
    }
},
}
</script>
<style>
div.v-subheader.theme--light {
  font-weight: bold !important;
  color: black !important;
  font-size: large !important;
  margin: 4px 0 0 12px
}
div .v-select__selections {
  padding-top: 8px !important;
}
</style>
